/** The merged Options & Trends tab (2026-07-27): live OI ladders and the
 * drift/trend charts on ONE screen. This component owns the drift-mode
 * toggle state and the oi-drift query, and derives ONE strike → color
 * mapping per side from the series order — so a strike's ladder bar and
 * its trend line always wear the same color. */

import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { ZoomButtons } from "@/components/ui/ZoomButtons";
import { marketApi } from "@/lib/marketApi";
import type { DriftMode, MarketIndex } from "@/types/market";
import { ModeToggle, POLL_MS } from "./driftShared";
import { MaxPainDrift } from "./MaxPainDrift";
import { OiChangeRow, strikeColorMap } from "./OiStrikeCharts";
import { OptionsPanel } from "./OptionsPanel";

export function OptionsTab({ index }: Readonly<{ index: MarketIndex }>): ReactElement {
  const [mode, setMode] = useState<DriftMode>("intraday");
  // Time zoom shared by all three trend charts (they read one clock):
  // divisor of the visible window, anchored to the latest points.
  const [zoom, setZoom] = useState(1);
  const oi = useQuery({
    queryKey: ["market", "oi-drift", index, mode],
    queryFn: () => marketApi.oiDrift(index, mode),
    refetchInterval: POLL_MS,
  });
  const strikeColors = {
    call: strikeColorMap(oi.data?.call ?? [], "call"),
    put: strikeColorMap(oi.data?.put ?? [], "put"),
  };
  // OI columns FIRST (they pair with the ladders above them), max pain
  // below — the operator reads ladders + OI trends on one screen
  // (2026-07-27 request). One toggle up top drives all three charts.
  return (
    <>
      <OptionsPanel index={index} strikeColors={strikeColors} />
      <section className="card mb-4">
        <div className="mb-2 flex justify-end gap-2">
          <ZoomButtons zoom={zoom} onChange={setZoom} />
          <ModeToggle value={mode} onChange={setMode} />
        </div>
        {/* The OI columns manage their own empty states — they must stay
            visible even while the max-pain series is empty, so the operator
            can always see the feature exists. */}
        <OiChangeRow data={oi.data} mode={mode} zoom={zoom} />
        <div className="mt-4">
          <MaxPainDrift index={index} mode={mode} zoom={zoom} />
        </div>
      </section>
    </>
  );
}
